import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { environment } from 'environments/environment';
import { RoleGuard } from './core/auth/guards/role.guard';
import { RoleTypes } from './core/auth/role-types';

const adminRoutes: Route[] = [
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('app/modules/admin/dashboard/dashboard.module').then((m) => m.DashboardModule),
      },

      // Apps
      {
        path: 'apps',
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [RoleTypes.practiceUser],
        },
        children: [
          {
            path: 'job',
            loadChildren: () => import('app/modules/admin/apps/job/job.module').then((m) => m.JobModule),
          },
          {
            path: 'companysearch',
            loadChildren: () =>
              import('app/modules/admin/apps/company-search/company-search.module').then((m) => m.CompanySearchModule),
          },
          {
            path: 'userconsole',
            loadChildren: () =>
              import('app/modules/admin/apps/user-console/user-console.module').then((m) => m.UserConsoleModule),
          },
          {
            path: 'add-job',
            loadChildren: () => import('app/modules/admin/apps/add-job/add-job.module').then((m) => m.AddJobModule),
          },
          {
            path: 'settings',
            loadChildren: () => import('app/modules/admin/apps/settings/settings.module').then((m) => m.SettingsModule),
          },
          // {
          //   path: 'tasks',
          //   loadChildren: () => import('app/modules/admin/apps/tasks/tasks.module').then((m) => m.TasksModule),
          // },
          {
            path: 'timesheet',
            loadChildren: () =>
              import('app/modules/admin/apps/timesheet/timesheet.module').then((m) => m.TimesheetModule),
          },
          {
            path: 'timereview',
            loadChildren: () =>
              import('app/modules/admin/apps/time-review/time-review.module').then((m) => m.TimeReviewModule),
          },
          {
            path: 'reports',
            loadChildren: () => import('app/modules/admin/apps/reports/reports.module').then((m) => m.ReportsModule),
          },
        ],
      },

      // Insol Admin
      {
        path: 'insol',
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [RoleTypes.insolsaasAdmin],
        },
        loadChildren: () => import('app/modules/insol/insol.module').then((m) => m.InsolModule),
      },
    ],
  },
];

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to '/example'
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

  // Redirect signed in user to the '/example'
  //
  // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

  // Auth routes for guests
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'confirmation-required',
        loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes'),
      },
      {
        path: 'forgot-password',
        loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes'),
      },
      { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
      { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
      { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') },
    ],
  },

  // Auth routes for authenticated users
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
      { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') },
    ],
  },

  // Landing routes
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      { path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') },
      { path: 'signin-oidc', loadChildren: () => import('app/modules/auth/signin-oidc/signin-oidc.routes') },
    ],
  },

  // Admin routes
  ...adminRoutes,

  // Redirect to dashboard if no matching routes
  {
    path: '**',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },
];

if (!environment.production || environment.includeSandboxRoute) {
  adminRoutes[0].children!.push({
    path: 'sandbox',
    loadChildren: () => import('app/modules/sandbox/sandbox.module').then((m) => m.SandboxModule),
  });
}
